import { useEffect, useRef } from "react";

/**
 * Publish an element's live height as a CSS custom property on <html>.
 *
 * The bottom sheet has to stop just below the top bar so that row stays visible
 * behind the backdrop — and the top bar's height is not a constant: a long
 * patient name wraps it, and the browser's own font settings move it. A
 * hardcoded offset would overlap the bar on one device and leave a gap on
 * another.
 *
 * Exactly the pattern PatientDetails.js:548-563 already uses for --ph-sticky-h,
 * including the ResizeObserver feature test — that API is missing on browsers
 * this app still supports, and the initial measurement alone is right for every
 * case except a reflow.
 *
 * `clear` removes the property again on unmount, so the stylesheet's fallback
 * takes over. The rows do not ask for it — their 52px fallback is the same
 * height, so a stale value is harmless. The call row does (Step 88): it comes
 * and goes mid-screen, and a stale 40px would leave every pinned row hanging
 * 40px below nothing once the call ends.
 *
 * @param {string} varName e.g. "--zym-row1-h"
 * @param {Array} deps re-measure when these change
 * @param {{clear?: boolean}} options
 * @returns {object} ref to attach to the element being measured
 */
const useCssVarHeight = (varName, deps = [], { clear = false } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const rootStyle = el.ownerDocument.documentElement.style;

    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      rootStyle.setProperty(varName, `${h}px`);
    };
    publish();

    const ro =
      typeof window.ResizeObserver === "function"
        ? new window.ResizeObserver(publish)
        : null;
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
      if (clear) rootStyle.removeProperty(varName);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varName, clear, ...deps]);

  return ref;
};

export default useCssVarHeight;
